<template>
  <div ref="superHero" v-editable="blok" class="split-text-form">
    <div class="split-text-form__content">
      <CustomRTE v-if="blok.text[0]" :blok="blok.text[0]" />
    </div>
    <div class="split-text-form__form">
      <div v-editable="blok" class="mr-auto container w-full max-w-[844px]">
        <div :id="formID" class="hubspot-form"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SplitFormTextBlokProps } from '@/types';

// Props
const props = defineProps<{
  blok: SplitFormTextBlokProps;
}>();

const formID = ref(
  props.blok.custom_id
    ? props.blok.custom_id
    : 'H-' + props.blok.form_id.replace(/-/g, '')
);

// Lifecycle : onMounted
onMounted(() => {
  if (props.blok.form_id) {
    window.hbspt.forms.create({
      formId: props.blok.form_id,
      formInstanceId: formID.value,
      portalId: '20991303',
      region: 'na1',
      target: '#' + formID.value,
    });
  }
});
</script>

<style lang="scss">
.split-text-form {
  @include container;

  margin-inline: auto;
  width: min(var(--content-width), 100%);

  @media all and #{$mq-m-min} {
    display: grid;
    grid-template-columns: auto 1fr;
  }

  /* Container for Content */
  &__content {
    display: flex;
    flex-direction: column;
    padding-block: var(--spacing-l);
    padding-block-start: var(--spacing-m);
    padding-inline-end: var(--spacing-m);
    padding-block-end: 0;

    @media all and #{$mq-m-min} {
      justify-content: start;
      padding-inline-end: columns(0.5);
      margin-inline-end: columns(1);
      max-width: columns(11);
    }
    @media all and #{$mq-l-min} {
      padding-inline-end: var(--side-spacing);
      max-width: columns(11);
    }
  }

  &__form {
    display: flex;
    flex-direction: column;

    @media all and #{$mq-m-min} {
      align-items: flex-end;
      justify-content: center;
    }
  }

  /* Adjust Styling of CTAGroup Component */
  .cta-group {
    margin-block-start: var(--spacing-s);

    @media all and #{$mq-s-max} {
      justify-content: start;
    }
  }
}

.hubspot-form input,
.hubspot-form select {
  width: 100%;
  height: 36px;
  overflow: hidden;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  padding-left: 5px;
  padding-right: 5px;
  margin-block-start: 0.875rem !important;
}

.field {
  margin-bottom: 1.25rem;
}

.hubspot-form input:focus,
.hubspot-form select:focus {
  outline: none !important;

  &:focus-visible {
    outline: 1px solid var(--lightest);
    outline-offset: 2px;
  }
}

.hubspot-form label {
  color: var(--neutral);
  font-size: 0.875rem;
}

form {
  margin-block-start: 40px;
  width: 100% !important;
  max-width: 844px !important;
  margin-inline-start: auto !important;
  margin-inline-end: auto !important;
}

[class^='form-columns-'] {
  width: 100% !important;
  max-width: 844px !important;
}
input,
select {
  outline: none;

  &:focus-visible {
    outline: 1px solid var(--lightest);
    outline-offset: 2px;
  }
}
input,
select {
  background-color: transparent;
  border: 1px solid var(--neutral);
  border-radius: 2px;
  min-height: 51px;
  padding-block: 12px;
  padding-inline: 24px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-width: 2px;
  }
}

input,
select {
  background-color: transparent;
  /* stylelint-disable-next-line value-keyword-case */
  border: 1px solid var(--neutral);
  border-radius: 2px;
  /* stylelint-disable-next-line value-keyword-case */
  color: currentColor;
  cursor: pointer;
  display: grid;
  font: inherit;
  height: 24px;
  margin: 3px 0 0;
  place-content: center;
  transform: translateY(-0.075em);
  width: 24px;

  &::before {
    /* stylelint-disable-next-line value-keyword-case */
    background-color: var(--lightest);
    box-shadow: inset 16px 16px var(--darkest);
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    content: '';
    height: 10px;
    transform: scale(0);
    transform-origin: bottom left;
    width: 10px;
  }

  &:focus-visible {
    /* stylelint-disable-next-line value-keyword-case */
    outline: max(2px, 0.15em) solid currentColor;
    outline-offset: max(2px, 0.15em);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:checked {
    /* stylelint-disable-next-line value-keyword-case */
    background-color: currentColor;
    /* stylelint-disable-next-line value-keyword-case */
    border-color: currentColor;

    &::before {
      transform: scale(1);
    }
  }
}

select {
  padding-right: 16px !important;
}

input.error,
select.error {
  border-color: var(--red) !important;
}
.hs-error-msgs {
  margin-block-end: 0;
}
.hs-error-msg {
  color: var(--red) !important;
  font-size: 0.75rem !important;
  inset-block-start: 100% !important;
  inset-inline-start: 0 !important;
  margin-block: 0 !important;
  margin-block-start: 4px !important;
}

.hs-form-required {
  color: green !important;
  font-weight: 900;
}
.hs-submit .actions {
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media all and #{$mq-xs-max} {
    align-items: flex-start;
  }

  @media all and #{$mq-xs-min} {
    flex-direction: row;
  }
}
input[type='submit'] {
  width: auto !important;
  align-items: center;
  display: flex;
  font-size: 0.875rem;
  justify-content: center;
  outline-offset: 4px;
  position: relative;
  white-space: nowrap;
  background: var(--dark-gray);
  color: #fff !important;
  text-align: center !important;
  cursor: pointer;
  border-radius: 2px !important;
  padding-block: 10px !important;
  padding-inline: 16px !important;
  text-decoration: none;

  @media all and #{$mq-m-min} {
    padding-block: 12px !important;
    padding-inline: 24px !important;
  }
}
/*input[type='submit']:hover {
  background: #0abc28 !important;
}*/
</style>
